import { getDB } from "@/db";
import { request } from "@/lib/request";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { isAxiosError } from "axios";
import { z } from "zod";

export const requestFormSchema = z.object({
  url: z.string().nonempty("URL can not be empty"),
  method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]),
  isPaginated: z.boolean().default(false),
  pagination: z
    .object({
      type: z.literal("cursor"),
      cursorType: z.enum(["index", "date"]),
    })
    .or(
      z.object({
        type: z.literal("basic"),
        countField: z.string().nonempty("Count Field can not be empty"),
      }),
    )
    .or(z.null()),
  queries: z
    .array(
      z.object({
        enabled: z.boolean(),
        pageField: z.boolean(),
        perPageField: z.boolean(),
        key: z.string().nonempty("Key can not be empty"),
        value: z.string(),
      }),
    )
    .superRefine((val, ctx) => {
      if (
        (val.some(v => v.pageField) && !val.some(v => v.perPageField)) ||
        (!val.some(v => v.pageField) && val.some(v => v.perPageField))
      ) {
        return ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "To enable pagination, both page and perPage fields are required",
        });
      }
    }),
  headers: z.array(
    z.object({
      enabled: z.boolean(),
      key: z.string().nonempty("Key can not be empty"),
      value: z.string(),
    }),
  ),
  auth: z.union([
    z.object({
      type: z.literal("none"),
    }),
    z.object({
      type: z.literal("basic"),
      username: z.string(),
      password: z.string(),
    }),
    z.object({
      type: z.literal("bearer"),
      token: z.string(),
      prefix: z.string(),
    }),
  ]),
  body: z.string(),
});

export type RequestConfigType = z.infer<typeof requestFormSchema> & {
  id: string;
};

export type ResponseType<T = unknown> = {
  data: T;
  status: number;
  loading: boolean;
  isError: boolean;
  error: Error | null;
};

export interface RequestsState {
  requests: Record<string, RequestConfigType>;
  responses: Record<string, ResponseType | null>;
  initialized: boolean;
  profiles: Record<string, Record<string, RequestConfigType>>;
}

const initialState: RequestsState = {
  requests: {},
  responses: {},
  profiles: {},
  initialized: false,
};

const handleApiError = (err: unknown, message: string) => {
  if (!isAxiosError(err) || !err.status?.toString().startsWith("4")) {
    console.error(message, err);
  }
};

export const initializeRequests = createAsyncThunk(
  "requests/initialize",
  async (_, { dispatch }) => {
    const requests = await (await getDB()).getAll("requests");
    if (request.defaults.headers.common.Authorization) {
      dispatch(syncBackend());
    }
    return requests || [];
  },
);

export const syncBackend = createAsyncThunk(
  "requests/syncBackend",
  async () => {
    const response = await request.get<{ data: RequestConfigType[] }>(
      "/internal/request",
    );
    const tx = (await getDB()).transaction("requests", "readwrite");
    await Promise.all([
      ...response.data.data.map(requestConfig => tx.store.put(requestConfig)),
      tx.done,
    ]);

    return response.data.data;
  },
);

export const setRequestConfig = createAsyncThunk(
  "requests/setRequestConfig",
  async (
    { requestConfig }: { requestConfig: RequestConfigType },
    { dispatch },
  ) => {
    await (await getDB()).put("requests", requestConfig);
    dispatch(updateRequestAPI({ requestConfig }));
    return requestConfig;
  },
);

export const deleteRequestConfig = createAsyncThunk(
  "requests/deleteRequestConfig",
  async ({ id }: { id: string }, { dispatch }) => {
    await (await getDB()).delete("requests", id);
    dispatch(deleteRequestAPI({ id }));
    return id;
  },
);

export const createRequestAPI = createAsyncThunk(
  "requests/createBackend",
  async ({ requestConfig }: { requestConfig: RequestConfigType }) => {
    const response = await request.post(`/internal/request`, requestConfig);
    return response.data.data;
  },
);

export const updateRequestAPI = createAsyncThunk(
  "requests/updateBackend",
  async (
    { requestConfig }: { requestConfig: RequestConfigType },
    { dispatch },
  ) => {
    try {
      const response = await request.put(
        `/internal/request/${requestConfig.id}`,
        requestConfig,
      );

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.status === 404) {
        dispatch(createRequestAPI({ requestConfig }));
      } else {
        throw error;
      }
    }
  },
);

export const linkRequest = createAsyncThunk(
  "requests/linkRequest",
  async ({
    id,
    profileId,
    elementId,
  }: {
    id: string;
    profileId: string;
    elementId: string;
  }) => {
    const response = await request.post(`/internal/request/${id}/link`, {
      profileId,
      elementId,
    });

    return response.data.data;
  },
);

export const unlinkRequest = createAsyncThunk(
  "requests/unlinkRequest",
  async ({ id, profileId }: { id: string; profileId: string }) => {
    await request.delete(`/internal/request/${id}/link`, {
      data: {
        profileId,
      },
    });
    return id;
  },
);

export const deleteRequestAPI = createAsyncThunk(
  "requests/deleteBackend",
  async ({ id }: { id: string }) => {
    await request.delete(`/internal/request/${id}`);
    return id;
  },
);

const requestsSlice = createSlice({
  name: "requests",
  initialState,
  reducers: {
    setResponse: (
      state,
      action: PayloadAction<{
        requestId: string;
        response: ResponseType | null;
      }>,
    ) => {
      const { requestId, response } = action.payload;
      state.responses[requestId] = response;
    },
    addProfileRequests: (
      state,
      action: PayloadAction<{
        profileId: string;
        requests: RequestConfigType[];
      }>,
    ) => {
      const { profileId, requests } = action.payload;
      state.profiles[profileId] = requests.reduce<
        Record<string, RequestConfigType>
      >((acc, request) => {
        acc[request.id] = request;
        return acc;
      }, {});
    },
    removeProfileRequests: (
      state,
      action: PayloadAction<{ profileId: string }>,
    ) => {
      const { profileId } = action.payload;
      delete state.profiles[profileId];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(initializeRequests.fulfilled, (state, action) => {
        state.requests = action.payload
          .sort((a, b) => a.url.localeCompare(b.url))
          .reduce<RequestsState["requests"]>((acc, requestConfig) => {
            acc[requestConfig.id] = requestConfig;

            return acc;
          }, {});
        state.initialized = true;
      })
      .addCase(initializeRequests.rejected, (state, action) => {
        state.requests = {};
        state.initialized = true;
        console.error("Failed to initialize requests:", action.error);
      })
      .addCase(syncBackend.fulfilled, (state, action) => {
        action.payload?.map((request: RequestConfigType) => {
          state.requests[request.id] = request;
        });
      })
      .addCase(setRequestConfig.fulfilled, (state, action) => {
        if (!action.payload?.id) {
          return;
        }

        state.requests[action.payload.id] = action.payload;
      })
      .addCase(setRequestConfig.rejected, (_, action) => {
        console.error("Failed to set request config:", action.error);
      })
      .addCase(deleteRequestConfig.fulfilled, (state, action) => {
        delete state.requests[action.payload];
        delete state.responses[action.payload];
      })
      .addCase(deleteRequestConfig.rejected, (_, action) => {
        console.error("Failed to delete request config:", action.error);
      })
      .addCase(createRequestAPI.fulfilled, (state, action) => {
        if (!action.payload?.id) {
          return;
        }

        state.requests[action.payload.id] = action.payload;
      })
      .addCase(updateRequestAPI.fulfilled, (state, action) => {
        if (!action.payload?.id) {
          return;
        }

        state.requests[action.payload.id] = action.payload;
      })
      .addCase(deleteRequestAPI.fulfilled, (state, action) => {
        delete state.requests[action.payload];
      })
      .addCase(syncBackend.rejected, (_, action) => {
        handleApiError(action.error, "Failed to sync requests with backend:");
      })
      .addCase(createRequestAPI.rejected, (_, action) => {
        handleApiError(action.error, "Failed to create request config:");
      })
      .addCase(updateRequestAPI.rejected, (_, action) => {
        handleApiError(action.error, "Failed to update request config:");
      })
      .addCase(deleteRequestAPI.rejected, (_, action) => {
        handleApiError(action.error, "Failed to delete request config:");
      });
  },
});

export const { setResponse, addProfileRequests, removeProfileRequests } =
  requestsSlice.actions;
export default requestsSlice.reducer;
